// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCategoriesDataView,
    getCategoryState,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import loadCategory from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategory";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Select, { SelectProps } from "@insite/mobius/Select";
import { TypographyProps } from "@insite/mobius/Typography";
import TitleTypography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import { trackAsImpersonation, trackGaProductCategoriesWidgetEvent } from "@stoneage/App/Utilities/DataLayer";
import LazyImage, { LazyImageProps } from "@stoneage/MobiusOverrides/LazyImage";
import Tab, { TabPresentationProps } from "@stoneage/MobiusOverrides/Tab/Tab";
import TabGroup, { TabGroupPresentationProps } from "@stoneage/MobiusOverrides/TabGroup/TabGroup";
import { xScreen } from "@stoneage/XDL/Utilities";
import React, { FC, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// TODO :: we need this code to be global somewhere, and tp updated on window resizing
export const isMobileScreen = () => typeof window !== "undefined" && window.matchMedia("(max-width: 1024px)").matches;

// for widget
const enum fields {
    title = "title",
    tabs = "tabs",
}

// tabs
const enum tabFields {
    title = "title",
    categoryList = "categoryList",
}

// categories
const enum categoryFields {
    overrideTitle = "overrideTitle",
    category = "category",
}

interface CategoryListProps {
    fields: {
        [categoryFields.overrideTitle]: string;
        [categoryFields.category]: string[];
    };
}

interface TabsProps {
    fields: {
        [tabFields.title]: string;
        [tabFields.categoryList]: CategoryListProps[];
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.tabs]: TabsProps[];
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    // prepeare data for the component
    const widgetData = {
        title: ownProps.fields?.title || "",
        tabs:
            ownProps.fields?.tabs?.map(tab => {
                return {
                    title: tab.fields?.title,
                    categories: tab.fields.categoryList.map(item => {
                        return {
                            title: item.fields.overrideTitle,
                            data: getCategoryState(state, item.fields.category[0])?.value,
                        };
                    }),
                };
            }) || [],
    };

    return {
        categoriesDataView: getCategoriesDataView(state),
        widgetData,
        impersonation: trackAsImpersonation(state),
    };
};

const mapDispatchToProps = {
    loadCategories,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ContentFeatureStyles {
    wrapper?: InjectableCss;
    title?: TypographyProps;
    tabGroupStyle?: TabGroupPresentationProps;
    tabStyle?: TabPresentationProps;
    categoriesContainer?: GridContainerProps;
    categoryWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    link?: LinkPresentationProps;
    mobileTopSection?: InjectableCss;
    select?: SelectProps;
    ItemContainerGridItem?: GridItemProps;
}

export const shopByStyles: ContentFeatureStyles = {
    wrapper: {
        css: css`
            position: relative;
        `,
    },
    title: {
        color: "common.background",
        lineHeight: "28px",
        size: 24,
        weight: "bold",
        css: css`
            ${xScreen("gt", "md")} {
                position: absolute;
                top: 0;
            }
            ${xScreen("lte", "md")} {
                display: flex;
                align-items: end;
            }
        `,
    },

    tabGroupStyle: {
        mergeCss: true,
        placement: "right",
        cssOverrides: {
            tabContent: css`
                padding: 15px 0;
                margin-top: 0;
                color: ${getColor("common.background")};
            `,
        },
    },
    tabStyle: {
        color: "common.ui3",
        css: css`
            &:first-child {
                text-align: center;
                padding-left: 12px;
            }
        `,
    },
    categoriesContainer: {
        gap: 0,
        css: css`
            > div {
                gap: 22px;
                ${xScreen("lte", "md")} {
                    gap: 60px;
                }
                ${xScreen("lt", "sm")} {
                    gap: 24px;
                }
            }
        `,
    },
    ItemContainerGridItem: {
        width: [6, 4, 4, 2, 2],
        css: css`
            &&& {
                max-width: calc(100% / 6 - 19px);
                ${xScreen("lte", "md")} {
                    max-width: calc(100% / 3 - 40px);
                }
                ${xScreen("lt", "sm")} {
                    max-width: calc(100% / 2 - 12px);
                }
            }
        `,
    },
    categoryWrapper: {
        css: css`
            background-color: ${getColor("common.background")};
            padding-bottom: 16px;
            max-width: 100%;
            height: 100%;
        `,
    },
    imageWrapper: {
        css: css`
            height: 165px;
            width: 198px;
            margin-bottom: 20px;
            display: flex;
            justify-content: center;
            align-items: center;
            max-width: 100%;
            max-height: 100%;

            & > div img {
                max-width: 100%;
                max-height: 100%;
            }
            ${xScreen("lte", "md")} {
                width: 100%;
            }
        `,
    },
    link: {
        color: "secondary",
        css: css`
            font-size: 15px;
            font-weight: bold;
            letter-spacing: 0;
            line-height: 18px;
            text-align: center;
            display: block;
            width: 100%;
            height: 100%;
            &&& {
                > span {
                    height: 100%;
                    &:hover,
                    &:focus {
                        color: ${getColor("primary.main")};
                    }
                }
            }
        `,
    },
    mobileTopSection: {
        css: css`
            display: flex;
            justify-content: space-between;
            padding-bottom: 15px;
            margin: 15px 0;
            border-bottom: 1px solid ${getColor("common.ui6")};

            ${xScreen("lt", "sm")} {
                flex-direction: column;
            }
        `,
    },
    select: {
        cssOverrides: {
            formField: css`
                max-width: 320px;

                ${xScreen("lte", "sm")} {
                    margin-top: 16px;
                }
                ${xScreen("lt", "sm")} {
                    max-width: initial;
                }
            `,
        },
    },
};

const styles = shopByStyles;

const CategoryList: FC<Props> = ({ fields, loadCategories, categoriesDataView, widgetData, impersonation }) => {
    const isMobile = isMobileScreen();
    const [selectedTab, setSelectedTab] = useState(widgetData.tabs?.[0]?.title);
    const itemsRef = useRef([]);

    useEffect(() => {
        if (!categoriesDataView.isLoading && !categoriesDataView.value) {
            loadCategories();
        }
    }, []);

    const renderCategoryItem = (tab: any) => (
        <GridContainer {...styles.categoriesContainer}>
            {tab?.categories?.map((category: any, index: any) => (
                <GridItem {...styles.ItemContainerGridItem} key={`${category?.data?.id}${index}`}>
                    <Link
                        href={category?.data?.path}
                        {...styles.link}
                        onClick={() => trackGaProductCategoriesWidgetEvent(category?.data?.path, impersonation)}
                    >
                        <StyledWrapper {...styles.categoryWrapper}>
                            <StyledWrapper {...styles.imageWrapper}>
                                <LazyImage
                                    src={category?.data?.smallImagePath}
                                    altText={category?.data?.shortDescription}
                                    cloudinary={true}
                                    width="198"
                                    height="165"
                                    isCatalogImage={true}
                                ></LazyImage>
                            </StyledWrapper>

                            {category?.title || category?.data?.shortDescription}
                        </StyledWrapper>
                    </Link>
                </GridItem>
            ))}
        </GridContainer>
    );

    const handleSelectCategoryTab: React.ChangeEventHandler<HTMLSelectElement> = event => {
        setSelectedTab(event.currentTarget.value);
    };

    if (isMobile) {
        const tab = widgetData.tabs.find(tab => tab?.title === selectedTab);

        return (
            <StyledWrapper {...styles.wrapper} data-test-selector="CategoryList">
                <StyledWrapper {...styles.mobileTopSection}>
                    {widgetData?.title && <TitleTypography {...styles.title}>{widgetData?.title}</TitleTypography>}

                    <Select onChange={handleSelectCategoryTab} {...styles.select}>
                        {widgetData.tabs.map(tab => {
                            const value = tab?.title;
                            return (
                                <option key={value} value={value}>
                                    {tab?.title}
                                </option>
                            );
                        })}
                    </Select>
                </StyledWrapper>

                {renderCategoryItem(tab)}
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="CategoryList">
            {widgetData?.title && <TitleTypography {...styles.title}>{widgetData?.title}</TitleTypography>}
            <TabGroup current={widgetData.tabs[0]?.title} {...styles.tabGroupStyle} className="dynamic-animation">
                {widgetData.tabs.map((tab, index) => {
                    return (
                        <Tab
                            id={`tab_${index}`}
                            currentTabIndex={index}
                            key={tab?.title}
                            headline={tab?.title}
                            tabKey={tab?.title}
                            {...styles.tabStyle}
                        >
                            {renderCategoryItem(tab)}
                        </Tab>
                    );
                })}
            </TabGroup>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CategoryList),
    definition: {
        displayName: "Shop by Category",
        group: "XNGAGE" as any,
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Widget Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.tabs,
                editorTemplate: "ListField",
                defaultValue: [],
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: tabFields.title,
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: tabFields.categoryList,
                        editorTemplate: "ListField",
                        defaultValue: [],
                        getDisplay: (item: HasFields, state) => {
                            if (item.fields.overrideTitle) {
                                return item.fields.overrideTitle;
                            }

                            if (item.fields.category[0]) {
                                const categoryState = getCategoryState(state, item.fields.category[0]);
                                if (!categoryState.value) {
                                    if (!categoryState.isLoading && item.fields.category[0]) {
                                        return dispatch => {
                                            dispatch(
                                                loadCategory({
                                                    id: item.fields.category[0],
                                                }),
                                            );
                                        };
                                    }
                                    return "";
                                }
                                return categoryState.value.shortDescription;
                            }

                            return "";
                        },
                        fieldDefinitions: [
                            {
                                name: categoryFields.overrideTitle,
                                editorTemplate: "TextField",
                                defaultValue: "",
                            },
                            {
                                name: categoryFields.category,
                                displayName: "Select Category",
                                editorTemplate: "CategoriesField",
                                singleCategorySelection: () => true,
                                defaultValue: [],
                            },
                        ],
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
